/* eslint-disable max-lines */
import { NetworkStatuses } from '@strata/constants';
import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { FormAccordion } from '@strata/react-form-section';
import { Hyperlink } from '@strata/react-hyperlink';
import { PhoneInputFormik, TextInputFormik } from '@strata/react-inputs';
import { DetailLabelList, DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { InformationIcon } from '@strata/react-status-icons';
import { validationStrings } from '@strata/strata-ui-strings';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import { ReferralSubmissionMessageContainer } from 'components/referralForm/ReferralSubmissionMessageContainer';
import { SectionLoadingSpinner } from 'components/referralForm/SectionLoadingSpinner';
import { useEligibleSuppliers } from 'services/APIHooks';
import { FMH_NPI, FMH_UNMANAGED_NPI, PlaceOfServiceCode, RadioOptions } from 'services/constants';
import { fetchSuppliers } from 'store/referralForm/referenceSlice';
import {
    completeSection,
    enableSection,
    updateCapturedData,
} from 'store/referralForm/referralFormSlice';

const SentToPharmacyReasonOptions = [
    'The pharmacy is the preferred pharmacy of the prescriber',
    'The pharmacy is the preferred pharmacy of the patient',
    'The pharmacy has been servicing the patient previously',
    'The insurance company mandates the use of this pharmacy',
    'The pharmacy completes our prior authorization work',
];

/* eslint-disable react/prop-types */
const TermsCheckbox = ({ termsRef }) => (
    <CheckboxListFormik
        name="terms"
        label="Terms and Conditions"
        required
        inputRef={(ref) => {
            if (termsRef) {
                termsRef.current = ref;
            }
        }}
        options={[
            {
                value: 'agreed',
                component: (
                    <span>
                        <span>{'I agree to the '}</span>
                        <Hyperlink linkText="Terms & Conditions." navUrl="terms-and-conditions" />
                    </span>
                ),
            },
        ]}
    />
);
/* eslint-enable react/prop-types */

export const ReferralSubmission = ({ section, isOpen, width }) => {
    const dispatch = useDispatch();

    const { capturedData, activeSection } = useSelector((state) => state.referralForm);
    const { payerId, segmentId, memberState, productServiceOid, placeOfService, billing } =
        capturedData;

    const authedPharmacies = useEligibleSuppliers({
        payerId,
        segmentId,
        memberState,
        productServiceOid,
        benefit: billing,
        placeOfService,
        fetchValues: isOpen,
    });

    // If placeOfService is FACILITY, INFU_INFU, or PHYS_PHYS referral is unmanaged.
    const unmanaged =
        placeOfService === PlaceOfServiceCode.FACILITY ||
        placeOfService === PlaceOfServiceCode.PHYS_PHYS ||
        placeOfService === PlaceOfServiceCode.INFU_INFU;

    // If no participants or npi is FMH or FMH_UNMANAGED, referral is office managed.
    const officeManaged =
        authedPharmacies?.length === 0 ||
        authedPharmacies?.find(
            (pharmacy) => pharmacy.npi === FMH_NPI || pharmacy.npi === FMH_UNMANAGED_NPI,
        );

    // Show terms checkbox only if referral is office managed or placeOfService is FACILITY, INFU_INFU, or PHYS_PHYS.
    const termsOnly = unmanaged || officeManaged;

    const { supplierList } = useSelector((state) => state.reference);
    useEffect(() => {
        if (isOpen && !supplierList) {
            dispatch(fetchSuppliers());
        }
    }, [isOpen, supplierList, dispatch]);

    const inNetworkPharmacies = useMemo(
        () => supplierList?.filter((supplier) => supplier.networkStatus === NetworkStatuses.ACTIVE),
        [supplierList],
    );

    const termsRef = useRef(null);
    const sentToPharmacyRef = useRef(null);
    useEffect(() => {
        if (isOpen && activeSection === section && authedPharmacies) {
            if (termsOnly) {
                setTimeout(() => termsRef.current?.focus(), FormAccordion.AnimateDuration);
            } else {
                setTimeout(() => sentToPharmacyRef.current?.focus(), FormAccordion.AnimateDuration);
            }
        }
    }, [isOpen, activeSection, section, authedPharmacies, termsOnly]);

    // Update section status when form is valid to enable submit button
    const [isSectionValid, setIsSectionValid] = useState();
    useEffect(() => {
        if (isSectionValid !== undefined) {
            dispatch(isSectionValid ? completeSection(section) : enableSection(section));
        }
    }, [isSectionValid, section, dispatch]);

    // Update captured values when form is valid
    const [formikValues, setFormikValues] = useState({});
    useEffect(() => {
        dispatch(updateCapturedData(formikValues));
    }, [formikValues, dispatch]);

    // Determine if formik state vars should be updated. Prevents duplicate & unintended updates
    const updateFormikState = (formik) => {
        if (isOpen && !formik.isValidating) {
            if (formik.isValid && formik.dirty) {
                setFormikValues(formik.values);
            }
            if (formik.isValid !== isSectionValid) {
                setIsSectionValid(formik.isValid);
            }
        }
    };

    return !authedPharmacies ? (
        <SectionLoadingSpinner />
    ) : (
        <Formik
            initialValues={capturedData}
            enableReinitialize
            validateOnMount
            validationSchema={Yup.object({
                terms: Yup.array().min(1, validationStrings.requiredField.errorMessage),
                ...(!termsOnly && {
                    sentToPharmacy: Yup.string().required(
                        validationStrings.requiredField.errorMessage,
                    ),
                    sentToAuthedPharmacy:
                        authedPharmacies.length === 1
                            ? Yup.string().required(validationStrings.requiredField.errorMessage)
                            : undefined,
                    sentToPharmacyNpi: Yup.string().when(
                        ['sentToPharmacy', 'sentToPharmacyNotListed'],
                        {
                            is: (sentToPharmacy, sentToPharmacyNotListed) =>
                                sentToPharmacy === RadioOptions.YES &&
                                sentToPharmacyNotListed.length === 0,
                            then: (schema) =>
                                schema.required(validationStrings.requiredField.errorMessage),
                        },
                    ),
                    sentToPharmacyNotListed: Yup.array().required(),
                    sentToPharmacyName: Yup.string().when('sentToPharmacy', {
                        is: (sentToPharmacy) => sentToPharmacy === RadioOptions.YES,
                        then: Yup.string().required(validationStrings.requiredField.errorMessage),
                        otherwise: Yup.string(),
                    }),
                    sentToPharmacyAddress: Yup.string().when(
                        ['sentToPharmacy', 'sentToPharmacyNotListed'],
                        {
                            is: (sentToPharmacy, sentToPharmacyNotListed) =>
                                sentToPharmacy === RadioOptions.YES &&
                                sentToPharmacyNotListed.length === 1,
                            then: (schema) =>
                                schema.required(validationStrings.requiredField.errorMessage),
                        },
                    ),
                    sentToPharmacyReason: Yup.string().when(
                        ['sentToPharmacy', 'sentToAuthedPharmacy'],
                        {
                            is: (sentToPharmacy, sentToAuthedPharmacy) =>
                                sentToPharmacy === RadioOptions.YES &&
                                sentToAuthedPharmacy !== RadioOptions.YES,
                            then: (schema) =>
                                schema.required(validationStrings.requiredField.errorMessage),
                        },
                    ),
                }),
                pointOfContact: Yup.string().required(validationStrings.requiredField.errorMessage),
                pointOfContactPhone: PhoneInputFormik.validator().required(
                    validationStrings.requiredField.errorMessage,
                ),
                pointOfContactFax: PhoneInputFormik.validator().required(
                    validationStrings.requiredField.errorMessage,
                ),
            })}
        >
            {(formik) => {
                updateFormikState(formik);

                const isSentToPharmacyNotListed =
                    formik.values.sentToPharmacyNotListed.length === 1;
                return (
                    <Form autoComplete="off" className="rta_referral-submission">
                        <div style={{ width, flexShrink: 0 }}>
                            {officeManaged && (
                                <DetailLabelValuePair
                                    className="padding__bottom--large"
                                    label="Office Managed Referral"
                                    value="This referral will be office managed and should be sent to an in-network pharmacy."
                                />
                            )}
                            {!termsOnly && (
                                <>
                                    <RadioButtonGroupFormik
                                        name="sentToPharmacy"
                                        label="Has the prescription been sent to a pharmacy?"
                                        required
                                        options={Object.values(RadioOptions)}
                                        onChange={(sentToPharmacy) =>
                                            formik.setValues({
                                                ...formik.values,
                                                sentToPharmacy,
                                                sentToAuthedPharmacy: undefined,
                                                sentToPharmacyNpi: undefined,
                                                sentToPharmacyNotListed: [],
                                                sentToPharmacyName: undefined,
                                                sentToPharmacyAddress: undefined,
                                                sentToPharmacyReason: undefined,
                                            })
                                        }
                                        inputRef={(ref) => {
                                            sentToPharmacyRef.current = ref;
                                        }}
                                    />
                                    {formik.values.sentToPharmacy !== undefined && (
                                        <>
                                            {authedPharmacies.length > 0 &&
                                                authedPharmacies.length <= 10 && (
                                                    <DetailLabelList
                                                        className="margin__bottom--medium"
                                                        label={`The patient's insurance plan has identified ${
                                                            authedPharmacies.length === 1
                                                                ? 'an authorized pharmacy'
                                                                : 'a few authorized pharmacies'
                                                        }`}
                                                        values={authedPharmacies.map(
                                                            (pharmacy) => pharmacy.name,
                                                        )}
                                                    />
                                                )}
                                            {formik.values.sentToPharmacy === RadioOptions.YES && (
                                                <>
                                                    {authedPharmacies.length === 1 && (
                                                        <RadioButtonGroupFormik
                                                            name="sentToAuthedPharmacy"
                                                            label={`Has the prescription been sent to ${authedPharmacies[0].name}?`}
                                                            required
                                                            options={Object.values(RadioOptions)}
                                                            onChange={(sentToAuthedPharmacy) =>
                                                                formik.setValues({
                                                                    ...formik.values,
                                                                    sentToAuthedPharmacy,
                                                                    sentToPharmacyNpi:
                                                                        sentToAuthedPharmacy ===
                                                                        RadioOptions.YES
                                                                            ? authedPharmacies[0]
                                                                            : undefined,
                                                                    sentToPharmacyNotListed: [],
                                                                    sentToPharmacyName: undefined,
                                                                    sentToPharmacyAddress:
                                                                        undefined,
                                                                    sentToPharmacyReason: undefined,
                                                                })
                                                            }
                                                        />
                                                    )}
                                                    {(authedPharmacies.length !== 1 ||
                                                        formik.values.sentToAuthedPharmacy ===
                                                            RadioOptions.NO) && (
                                                        <>
                                                            <SuggestSingleSelectFormik
                                                                id="sentToPharmacyNpi"
                                                                label="Pharmacy Where Prescription Was Sent"
                                                                required={
                                                                    formik.values
                                                                        .sentToPharmacyNotListed
                                                                        ?.length < 1
                                                                }
                                                                disabled={isSentToPharmacyNotListed}
                                                                values={inNetworkPharmacies}
                                                                isLoading={!inNetworkPharmacies}
                                                                onChange={(pharmacy) =>
                                                                    formik.setValues({
                                                                        ...formik.values,
                                                                        sentToPharmacyNpi:
                                                                            pharmacy?.npi,
                                                                        sentToPharmacyName:
                                                                            pharmacy?.name,
                                                                    })
                                                                }
                                                                selectedItem={inNetworkPharmacies?.find(
                                                                    (pharmacy) =>
                                                                        pharmacy.npi ===
                                                                        formik.values
                                                                            .sentToPharmacyNpi,
                                                                )}
                                                            />
                                                            <CheckboxListFormik
                                                                name="sentToPharmacyNotListed"
                                                                onChange={(
                                                                    sentToPharmacyNotListed,
                                                                ) =>
                                                                    formik.setValues({
                                                                        ...formik.values,
                                                                        sentToPharmacyNotListed,
                                                                        sentToPharmacyNpi:
                                                                            undefined,
                                                                        sentToPharmacyName:
                                                                            undefined,
                                                                        sentToPharmacyAddress:
                                                                            undefined,
                                                                    })
                                                                }
                                                                options={[
                                                                    {
                                                                        value: 'notListed',
                                                                        component: (
                                                                            <span
                                                                                style={{
                                                                                    display:
                                                                                        'inline-flex',
                                                                                    alignItems:
                                                                                        'center',
                                                                                }}
                                                                            >
                                                                                <span className="padding__right--small">
                                                                                    Pharmacy is not
                                                                                    listed.
                                                                                </span>
                                                                                <InformationIcon
                                                                                    tooltipText={
                                                                                        <div
                                                                                            style={{
                                                                                                width: 400,
                                                                                            }}
                                                                                        >
                                                                                            The
                                                                                            above
                                                                                            list
                                                                                            contains
                                                                                            authorized
                                                                                            pharmacies
                                                                                            identified
                                                                                            by the
                                                                                            patient&apos;s
                                                                                            insurance
                                                                                            plan. If
                                                                                            the
                                                                                            pharmacy
                                                                                            is not
                                                                                            listed,
                                                                                            you will
                                                                                            be asked
                                                                                            to enter
                                                                                            the
                                                                                            pharmacy
                                                                                            name and
                                                                                            address.
                                                                                        </div>
                                                                                    }
                                                                                />
                                                                            </span>
                                                                        ),
                                                                    },
                                                                ]}
                                                            />
                                                            {isSentToPharmacyNotListed && (
                                                                <>
                                                                    <TextInputFormik
                                                                        id="sentToPharmacyName"
                                                                        label="Pharmacy Name"
                                                                        required
                                                                    />
                                                                    <TextInputFormik
                                                                        id="sentToPharmacyAddress"
                                                                        label="Pharmacy Address"
                                                                        required
                                                                    />
                                                                </>
                                                            )}
                                                            <SuggestSingleSelectFormik
                                                                id="sentToPharmacyReason"
                                                                label="Reason"
                                                                required
                                                                values={SentToPharmacyReasonOptions}
                                                            />
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                            <TermsCheckbox termsRef={termsRef} />
                            <div className="rta_intake-form__field-group-section">
                                Point Of Contact For Referral
                            </div>
                            <TextInputFormik id="pointOfContact" label="Name" required />
                            <PhoneInputFormik
                                id="pointOfContactPhone"
                                label="Phone Number"
                                required
                            />
                            <PhoneInputFormik id="pointOfContactFax" label="Fax Number" required />
                        </div>
                        {(formik.values.sentToPharmacy !== undefined ||
                            (!unmanaged && officeManaged)) && (
                            <ReferralSubmissionMessageContainer
                                sentToPharmacy={formik.values.sentToPharmacy === RadioOptions.YES}
                                pharmacyCount={authedPharmacies.length}
                                officeManaged={officeManaged}
                            />
                        )}
                    </Form>
                );
            }}
        </Formik>
    );
};

ReferralSubmission.propTypes = {
    section: PropTypes.number.isRequired,
    isOpen: PropTypes.bool.isRequired,
    width: PropTypes.string.isRequired,
};
